import { Injectable, inject } from '@angular/core';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { LoggerService } from './logger.service';

/**
 * Service for scanning QR codes and 1D barcodes using @zxing/browser
 * Handles camera access and multi-format code detection
 */
@Injectable({
  providedIn: 'root',
})
export class QrScannerService {
  private controls: IScannerControls | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private logger = inject(LoggerService).getLogger('qr-scanner');

  private readonly hints = new Map<DecodeHintType, any>([
    [
      DecodeHintType.POSSIBLE_FORMATS,
      [BarcodeFormat.QR_CODE, BarcodeFormat.CODE_128, BarcodeFormat.EAN_13, BarcodeFormat.CODE_39],
    ],
  ]);

  /**
   * Get list of available video input devices
   */
  async getVideoDevices(): Promise<MediaDeviceInfo[]> {
    this.logger.debug('Getting video devices...');
    const devices = await BrowserMultiFormatReader.listVideoInputDevices();
    this.logger.debug(
      'Found devices:',
      devices.map((d) => ({ id: d.deviceId, label: d.label })),
    );
    return devices;
  }

  /**
   * Start continuous decoding from video device
   */
  async startDecoding(
    deviceId: string,
    videoElement: HTMLVideoElement | undefined,
    callback: (result: string | null, format?: string) => void,
  ): Promise<void> {
    this.logger.debug('Starting decoding with deviceId:', deviceId);
    this.logger.debug('Video element:', videoElement ? 'provided' : 'undefined');

    // Clean up any existing stream first
    this.logger.debug('Stopping any existing stream...');
    this.stop();

    this.logger.debug('Creating new BrowserMultiFormatReader...');
    const codeReader = new BrowserMultiFormatReader(this.hints);
    this.videoElement = videoElement || null;

    try {
      this.logger.debug('Calling decodeFromVideoDevice...');
      this.controls = await codeReader.decodeFromVideoDevice(
        deviceId,
        videoElement,
        (result, error) => {
          if (result) {
            const format = BarcodeFormat[result.getBarcodeFormat()] || 'UNKNOWN';
            this.logger.debug('Code detected:', result.getText(), 'format:', format);
            callback(result.getText(), format);
          }
          if (error && error.name !== 'NotFoundException') {
            this.logger.warn('Decode error:', error.name, error.message);
          }
        },
      );

      this.logger.debug(
        'decodeFromVideoDevice completed, controls:',
        this.controls ? 'active' : 'null',
      );

      // Check video element state
      if (videoElement) {
        const computedStyle = window.getComputedStyle(videoElement);
        this.logger.debug('Video element state:', {
          srcObject: videoElement.srcObject ? 'set' : 'null',
          readyState: videoElement.readyState,
          videoWidth: videoElement.videoWidth,
          videoHeight: videoElement.videoHeight,
          paused: videoElement.paused,
          display: computedStyle.display,
          visibility: computedStyle.visibility,
          opacity: computedStyle.opacity,
          width: computedStyle.width,
          height: computedStyle.height,
          position: computedStyle.position,
        });

        if (videoElement.srcObject) {
          const stream = videoElement.srcObject as MediaStream;
          const tracks = stream.getTracks();
          this.logger.debug(
            'MediaStream tracks:',
            tracks.map((t) => ({
              kind: t.kind,
              enabled: t.enabled,
              readyState: t.readyState,
              label: t.label,
              muted: t.muted,
              id: t.id,
            })),
          );

          // Check if we're actually getting video frames
          const videoTrack = tracks.find((t) => t.kind === 'video');
          if (videoTrack) {
            this.logger.debug('Video track details:', {
              readyState: videoTrack.readyState,
              muted: videoTrack.muted,
              enabled: videoTrack.enabled,
              settings: videoTrack.getSettings(),
            });
          }
        } else {
          this.logger.error('No srcObject on video element after decoding started!');
        }
      }
    } catch (error: any) {
      this.logger.error('Failed to start decoding:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
      });
      throw error;
    }
  }

  /**
   * Stop decoding and release camera resources
   */
  stop(): void {
    this.logger.debug('Stopping scanner...');

    if (this.controls) {
      this.logger.debug('Stopping controls...');
      this.controls.stop();
      this.controls = null;
    }

    // Explicitly stop all media stream tracks and clear video element
    if (this.videoElement) {
      this.logger.debug('Cleaning up video element...');
      const stream = this.videoElement.srcObject as MediaStream;
      if (stream) {
        const tracks = stream.getTracks();
        this.logger.debug('Stopping', tracks.length, 'media track(s)...');
        tracks.forEach((track) => {
          this.logger.debug('Stopping track:', track.kind, track.label, track.readyState);
          track.stop();
        });
        this.videoElement.srcObject = null;
      }
      this.videoElement = null;
    }

    this.logger.debug('Stop complete');
  }
}
